import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tag,
  Card,
  List,
  Typography,
  Space,
  Button,
  Grid,
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { Lead, OpportunityStatus } from '../../types';
import { DataTable, type AntDColumn } from '../common/DataTable';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface OpportunitiesTableProps {
  opportunities: Lead[];
  onEdit?: (lead: Lead) => void;
  onDelete?: (lead: Lead) => void;
  onView?: (lead: Lead) => void;
  loading?: boolean;
  leads: Lead[];
}

const OpportunitiesTable: React.FC<OpportunitiesTableProps> = ({
  opportunities,
  onEdit,
  onView,
  loading = false,
  leads,
}) => {
  const { t } = useTranslation(['common', 'sales']);
  const screens = useBreakpoint();

  const getStageAntColor = (stage: OpportunityStatus): string => {
    switch (stage) {
      case 'Prospecting': return 'default';
      case 'Qualification': return 'geekblue';
      case 'Proposal': return 'blue';
      case 'Negotiation': return 'orange';
      case 'Closed Won': return 'green';
      case 'Closed Lost': return 'red';
      default: return 'default';
    }
  };

  const columns: AntDColumn<Lead>[] = [
    {
      id: 'lead',
      label: t('sales:opportunities.table.lead', 'Lead'),
      dataIndex: 'id',
      key: 'lead',
      width: 200,
      render: (_: any, record: Lead) => {
        const lead = leads.find(l => l.id === record.id);
        return (
          <div onClick={() => onView && onView(record)} style={{ cursor: 'pointer' }}>
            {lead ? lead.name : ''}
          </div>
        );
      },
    },
    {
      id: 'units',
      label: t('sales:opportunities.table.units', 'Units'),
      dataIndex: ['opportunity', 'units'],
      key: 'units',
      width: 100,
      align: 'right',
      render: (_: any, record: Lead) => record.opportunity?.units ?? '',
    },
    {
      id: 'stage',
      label: t('sales:opportunities.table.stage', 'Stage'),
      dataIndex: ['opportunity', 'stage'],
      key: 'stage',
      width: 150,
      render: (_: any, record: Lead) => (
        typeof record.opportunity?.stage === 'string' ? (
          <Tag color={getStageAntColor(record.opportunity.stage)}>
            {t('sales:opportunities.stages.' + record.opportunity.stage.toLowerCase())}
          </Tag>
        ) : '-'
      ),
    },
    {
      id: 'value',
      label: t('sales:opportunities.table.value', 'Value'),
      dataIndex: 'opportunity',
      key: 'value',
      width: 150,
      align: 'right',
      render: (_: any, record: Lead) =>
        record.opportunity?.value ? new Intl.NumberFormat(t('common:locale', 'en-US'), {
          style: 'currency',
          currency: record.opportunity.value?.currency || 'USD',
        }).format(record.opportunity.value?.amount ?? 0) : '',
    },
    {
      id: 'expected_close_date',
      label: t('sales:opportunities.table.expected_close_date', 'Expected Close Date'),
      dataIndex: ['opportunity', 'expected_close_date'],
      key: 'expected_close_date',
      width: 150,
      render: (_: any, record: Lead) => record.opportunity?.expected_close_date ? dayjs(record.opportunity.expected_close_date).format('YYYY-MM-DD') : '',
    },
    {
      id: 'created_at',
      label: t('sales:opportunities.table.created_at', 'Created At'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: Date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      id: 'actions',
      label: t('common:actions', 'Actions'),
      key: 'actions',
      width: 200,
      render: (_: any, record: Lead) => (
        <Space>
          {onView && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            >
              {t('common:view', 'View')}
            </Button>
          )}
          {onEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            >
              {t('common:edit', 'Edit')}
            </Button>
          )}
        </Space>
      ),
    }
  ];

  if (screens.xs) {
    return (
      <List
        grid={{ gutter: 16, xs: 1, sm: 1 }}
        dataSource={opportunities}
        loading={loading}
        renderItem={(opportunity: Lead) => (
          <List.Item key={opportunity.id}>
            <Card
              title={<Text strong>{leads.find(l => l.id === opportunity.id)?.name || 'N/A'}</Text>}
              style={{ width: '100%' }}
              actions={[
                onView && (
                  <Button 
                    key="view" 
                    type="link" 
                    icon={<EyeOutlined />} 
                    onClick={() => onView(opportunity)}
                  >
                    {t('common:view', 'View')}
                  </Button>
                ),
                onEdit && (
                  <Button 
                    key="edit" 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => onEdit(opportunity)}
                  >
                    {t('common:edit', 'Edit')}
                  </Button>
                )
              ].filter(Boolean) as React.ReactNode[]}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  <Text strong>{t('sales:opportunities.table.value')}: </Text>
                  {new Intl.NumberFormat(t('common:locale', 'en-US'), {
                    style: 'currency',
                    currency: opportunity.opportunity?.value?.currency || 'USD',
                  }).format(opportunity.opportunity?.value?.amount ?? 0)}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:opportunities.table.units')}: </Text>
                  {opportunity.opportunity?.units}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:opportunities.table.stage')}: </Text>
                  {typeof opportunity.opportunity?.stage === 'string' ? (
                    <Tag color={getStageAntColor(opportunity.opportunity.stage)}>
                      {t(`sales:opportunities.stages.${opportunity.opportunity.stage.toLowerCase()}`)}
                    </Tag>
                  ) : '-'}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:opportunities.table.expected_close_date')}: </Text>
                  {dayjs(opportunity.opportunity?.expected_close_date).format('YYYY-MM-DD')}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:opportunities.table.created_at')}: </Text>
                  {dayjs(opportunity.created_at).format('YYYY-MM-DD HH:mm')}
                </Paragraph>
              </Space>
            </Card>
          </List.Item>
        )}
      />
    );
  }

  return (
    <DataTable<Lead>
      columns={columns}
      data={opportunities}
      getRowId={(row: Lead) => row.id}
      loading={loading}
      scroll={{ x: 1300 }}
    />
  );
};

export default OpportunitiesTable; 